import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Field, PrimaryButton, inputClass } from '@/components/Card';
import { Chip, ChipRow } from '@/components/ChipRow';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { CalendarStatus } from '@shared/types';

export function ProfilePage() {
  const { user, setUser } = useAuth();
  const [displayName, setDisplayName] = useState(user?.profile.displayName ?? '');
  const [homeAirport, setHomeAirport] = useState(user?.profile.homeAirport ?? 'EWR');
  const [typicalAirline, setTypicalAirline] = useState(user?.profile.typicalAirline ?? 'UA');
  const [notes, setNotes] = useState(user?.profile.notes ?? '');
  const [saved, setSaved] = useState(false);
  const [calendar, setCalendar] = useState<CalendarStatus | null>(null);

  useEffect(() => {
    api.calendarStatus().then(setCalendar).catch(() => undefined);
  }, []);

  if (!user) return null;

  return (
    <div className="space-y-4">
      <Card className="space-y-4">
        <h2 className="font-display text-[30px] leading-none text-navy">Profile</h2>
        <ChipRow>
          <Chip tone={user.mode === 'guest' ? 'brass' : 'navy'}>{user.mode}</Chip>
          {user.admin && <Chip tone="success">Admin</Chip>}
        </ChipRow>
        <Field label="Display name">
          <input className={inputClass} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </Field>
        <Field label="Home airport">
          <select className={inputClass} value={homeAirport ?? ''} onChange={(e) => setHomeAirport(e.target.value)}>
            <option value="EWR">EWR</option>
            <option value="PBI">PBI</option>
            <option value="RSW">RSW</option>
            <option value="BNA">BNA</option>
          </select>
        </Field>
        <Field label="Typical airline">
          <input className={inputClass} value={typicalAirline} onChange={(e) => setTypicalAirline(e.target.value)} />
        </Field>
        <Field label="Notes">
          <textarea className={inputClass} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <PrimaryButton
          onClick={async () => {
            const data = await api.profile({ displayName, homeAirport, typicalAirline, notes });
            setUser(data.user);
            setSaved(true);
          }}
        >
          Save profile
        </PrimaryButton>
        {saved && <p className="text-sm text-success">Saved.</p>}
      </Card>
      <Card className="space-y-3">
        <h3 className="font-display text-2xl text-navy">Google Calendar</h3>
        <ChipRow>
          <Chip tone={calendar?.connected ? 'success' : 'line'}>{calendar?.connected ? 'Connected' : 'Not connected'}</Chip>
          <Chip tone={calendar?.oauthConfigured ? 'navy' : 'brass'}>
            {calendar?.oauthConfigured ? 'OAuth ready' : 'Demo mode'}
          </Chip>
          {calendar?.householdShared && <Chip>Household shared</Chip>}
        </ChipRow>
        <p className="text-sm text-muted">{calendar?.notice}</p>
        {calendar?.calendarEmail && <p className="text-[13px] text-muted">{calendar.calendarEmail}</p>}
        <p className="text-[12px] text-faint">
          Primary calendar for Chip is chipwilkes@gmail.com. Redirect URI this Worker will use:{' '}
          {calendar?.redirectUri ?? '…'}
        </p>
        <Link to="/trips/import" className="block rounded-card bg-navy px-4 py-3 text-center text-sm font-semibold text-sand">
          Import from Google Calendar
        </Link>
      </Card>
    </div>
  );
}
